import { aboutInfo } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫之家 - 韩国漫画免费阅读平台',
    description: '韩漫之家是国内领先的韩国漫画在线阅读平台，提供海量免费韩漫资源，包括恋爱、奇幻、动作、悬疑等多种分类。',
    url: 'https://hanmanzhijia.com/about',
    about: {
      '@type': 'Organization',
      name: '韩漫之家',
      description: aboutInfo.introduction,
      email: aboutInfo.contact.email,
      telephone: aboutInfo.contact.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '望京SOHO T1-1208',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <article>
            {/* Hero */}
            <section className="text-center py-12 md:py-16">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading text-foreground-950">
                关于<strong className="text-primary-500">韩漫之家</strong>
              </h1>
              <p className="mt-4 text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                {aboutInfo.slogan}
              </p>
            </section>

            {/* Stats */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-lg bg-background-100">
                  <p className="text-2xl md:text-3xl font-heading text-primary-500">{stat.value}</p>
                  <p className="mt-1 text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </section>

            {/* Introduction */}
            <section className="mb-16">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-8">
                  品牌<strong className="text-primary-500">介绍</strong>
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  {aboutInfo.introduction}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                  <div className="p-6 rounded-lg bg-background-100">
                    <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-950 mb-3">
                      <i className="ri-flag-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                      <strong>我们的使命</strong>
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
                  </div>
                  <div className="p-6 rounded-lg bg-background-100">
                    <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-950 mb-3">
                      <i className="ri-eye-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                      <strong>我们的愿景</strong>
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.vision}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Features */}
            <section className="mb-16 py-12 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-10">
                为什么选择<strong className="text-primary-500">韩漫之家</strong>？
              </h2>
              <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {aboutInfo.features.map((feature) => (
                  <div key={feature.title} className="bg-background-50 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-50 flex items-center justify-center">
                      <i className={`${feature.icon} text-xl text-primary-500`}></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-2">
                      <strong>{feature.title}</strong>
                    </h4>
                    <p className="text-xs text-foreground-500 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonials */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-10">
                用户<strong className="text-primary-500">口碑</strong>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutInfo.testimonials.map((testimonial) => (
                  <div key={testimonial.name} className="p-5 rounded-lg bg-background-100">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${
                            i < testimonial.rating
                              ? 'ri-star-fill text-accent-500'
                              : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      "{testimonial.content}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary-500">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground-900">{testimonial.name}</p>
                        <p className="text-xs text-foreground-400">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-10">
                <strong>联系</strong>我们
              </h2>
              <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="p-5 rounded-lg bg-background-100 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-50 flex items-center justify-center">
                    <i className="ri-mail-line text-lg text-primary-500"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h4>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.email}</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-50 flex items-center justify-center">
                    <i className="ri-phone-line text-lg text-primary-500"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-950 mb-1">客服电话</h4>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.phone}</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-50 flex items-center justify-center">
                    <i className="ri-map-pin-line text-lg text-primary-500"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h4>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.address}</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-50 flex items-center justify-center">
                    <i className="ri-wechat-line text-lg text-primary-500"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-950 mb-1">微信公众号</h4>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.wechat}</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-50 flex items-center justify-center">
                    <i className="ri-weibo-line text-lg text-primary-500"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-950 mb-1">微博</h4>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.weibo}</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-50 flex items-center justify-center">
                    <i className="ri-time-line text-lg text-primary-500"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-950 mb-1">工作时间</h4>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.workingHours}</p>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-10">
                常见<strong className="text-primary-500">问题</strong>
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {aboutInfo.faqs.map((faq, index) => (
                  <details key={index} className="group rounded-lg bg-background-100 overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <h4 className="text-sm font-medium text-foreground-950 pr-4">
                        <strong>{faq.question}</strong>
                      </h4>
                      <i className="ri-add-line text-foreground-400 group-open:hidden w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </article>
        </div>
      </main>
    </>
  );
}